import { Avatar, Card, EmptyState, Eyebrow, StatusPill, cn, type StatusTone } from '@homecare/ui';
import type { ClientStatus } from '@homecare/domain';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

import { AssistanceProfileCard, type AssistanceEntry } from './assistance-profile';
import { AuthorizationsPanel, PayerRulesEditor } from './authorizations';
import { AddressesCard, ContactsCard, type AddressRow, type ContactRow } from './people-and-places';
import {
  AllergiesCard,
  MedicationsCard,
  type AllergyRow,
  type MedicationRow,
} from './clinical-lists';
import { StatusActions } from './status-actions';
import { CoveragePanel, type CoverageSource } from './coverage';
import { ClientRelationshipsCard } from '../caregivers/client-relationships';
import { ClientVisits } from '../schedule/client-visits';
import { CarePlanPanel } from './care-plan';

/**
 * One client (§7).
 *
 * Header with name, number, status and primary payer, then six tabs. Care plan
 * and Visits render empty states naming what will live there — **not** stubbed
 * with fake content, because a screen full of plausible invented visits is
 * indistinguishable from a broken one, and somebody will screenshot it.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  REFERRAL: 'scheduled',
  INTAKE: 'progress',
  ACTIVE: 'verified',
  ON_HOLD: 'warning',
  DISCHARGED: 'inactive',
};

const STATUS_LABEL: Record<string, string> = {
  REFERRAL: 'Referral',
  INTAKE: 'Intake',
  ACTIVE: 'Active',
  ON_HOLD: 'On hold',
  DISCHARGED: 'Discharged',
};

const TABS = ['Overview', 'Care plan', 'Visits', 'Authorizations', 'Documents', 'History'] as const;

type Tab = (typeof TABS)[number];

interface ClientDetail {
  id: string;
  mrn: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  dateOfBirth: string;
  status: string;
  primaryLanguage?: string | null;

  /*
   * PHI, and absent rather than null for a caller without `client:read_phi` —
   * the redaction interceptor strips the keys, it does not blank them. So
   * undefined means 'not permitted' and null means 'permitted, and not
   * recorded'. The card renders different sentences for the two, because they
   * are different facts.
   */
  ssnLast4?: string | null;
  medicaidId?: string | null;
  medicareId?: string | null;
  addresses?: AddressRow[];
  contacts?: ContactRow[];
  preferences?: { id: string; key: string; value: string; isHardRequirement: boolean }[];
  allergies?: AllergyRow[];
  medications?: MedicationRow[];
  /**
   * Absent when the caller lacks the clinical-records permission — the
   * response interceptor removes the key rather than emptying it (§8, layer
   * four), so undefined and an empty array mean different things here and the
   * card says which it is.
   */
  assistanceProfile?: AssistanceEntry[];
}

export function ClientDetail({ clientId }: { clientId: string }): ReactNode {
  const api = useApi();
  const canEdit = useHasPermission('client:update');
  const canDischarge = useHasPermission('client:discharge');
  const canReadAuths = useHasPermission('auth:read');
  const canCreateAuths = useHasPermission('auth:create');
  const canUpdateAuths = useHasPermission('auth:update');
  const [tab, setTab] = useState<Tab>('Overview');

  const client = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => api.get<ClientDetail>(`/v1/clients/${clientId}`),
  });

  const coverage = useQuery({
    queryKey: ['clients', 'coverage', clientId],
    queryFn: () => api.get<{ sources: CoverageSource[] }>(`/v1/clients/${clientId}/coverage`),
    // Coverage is a derived read; a client with no payers answers with an empty
    // list rather than a 404, so a failure here is a real failure.
    retry: false,
  });

  if (client.isPending) {
    return (
      <Card>
        <p className="text-ink-3 py-8 text-center text-sm">Loading this client…</p>
      </Card>
    );
  }

  if (client.error || !client.data) {
    return (
      <ErrorNote
        error={client.error}
        nextStep="Go back to the client list and try again, or ask an administrator to check your access."
      />
    );
  }

  const detail = client.data;
  const name = `${detail.firstName} ${detail.lastName}`;
  const primaryPayer = coverage.data?.sources[0]?.payerName ?? null;

  return (
    <div className="grid gap-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar name={name} seed={detail.id} size="md" />
            <div>
              <h1 className="text-ink text-display font-bold">{name}</h1>
              <p className="text-ink-3 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-mono" data-numeric>
                  {detail.mrn}
                </span>
                <span aria-hidden="true">·</span>
                <span>Born {detail.dateOfBirth}</span>
                {primaryPayer && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{primaryPayer}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="grid justify-items-end gap-3">
            <StatusPill tone={STATUS_TONE[detail.status] ?? 'inactive'}>
              {STATUS_LABEL[detail.status] ?? detail.status}
            </StatusPill>

            {/*
              Beside the pill, because the pill is what a person looks at when
              they want to change it. A status action buried in a tab is one
              nobody finds — which is close to what happened here, except there
              was no action at all.
            */}
            <StatusActions
              clientId={clientId}
              status={detail.status as ClientStatus}
              canUpdate={canEdit}
              canDischarge={canDischarge}
            />
          </div>
        </div>
      </Card>

      {/*
        Tabs as a real tablist, so a keyboard user gets arrow-key movement and a
        screen reader is told which panel is showing. Six links in a row that
        merely change colour would be six links.
      */}
      <div role="tablist" aria-label="Client sections" className="flex flex-wrap gap-1">
        {TABS.map((name_) => (
          <button
            key={name_}
            type="button"
            role="tab"
            id={`tab-${slug(name_)}`}
            aria-selected={tab === name_}
            aria-controls={`panel-${slug(name_)}`}
            onClick={() => setTab(name_)}
            className={cn(
              'duration-fast ease-calm min-h-web-min rounded-md px-4 py-2 text-sm transition-colors',
              tab === name_
                ? 'bg-brand text-ink-inv font-semibold'
                : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
            )}
          >
            {name_}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`panel-${slug(tab)}`}
        aria-labelledby={`tab-${slug(tab)}`}
        className="grid gap-5"
      >
        {tab === 'Overview' && (
          <>
            <CoveragePanel sources={coverage.data?.sources ?? []} />
            <Overview detail={detail} clientId={clientId} canEdit={canEdit} />
          </>
        )}

        {tab === 'Care plan' && <CarePlanPanel clientId={clientId} />}

        {tab === 'Visits' && <ClientVisits clientId={clientId} />}

        {tab === 'Authorizations' && (
          <>
            {/*
              Coverage first, because it is the answer — who pays for the next
              visit — and the two panels below it are where that answer comes
              from. A coordinator who only wants the answer stops reading after
              the first card.
            */}
            <CoveragePanel sources={coverage.data?.sources ?? []} />
            <AuthorizationsPanel
              clientId={clientId}
              canRead={canReadAuths}
              canCreate={canCreateAuths}
              canUpdate={canUpdateAuths}
            />
            <PayerRulesEditor clientId={clientId} canEdit={canEdit} />
          </>
        )}

        {tab === 'Documents' && (
          <Card padded={false}>
            <EmptyState
              title="No documents yet"
              explanation="Signed service agreements, physician orders and anything else this client's file needs to survive an audit."
            />
          </Card>
        )}

        {tab === 'History' && <History clientId={clientId} />}
      </div>
    </div>
  );
}

const slug = (value: string): string => value.toLowerCase().replace(/[^a-z]+/g, '-');

/** The preference keys, as a person would say them. */
const PREFERENCE_LABEL: Record<string, string> = {
  CAREGIVER_GENDER: 'Caregiver gender',
  LANGUAGE: 'Language',
  SMOKING: 'Smoking',
  PETS: 'Pets',
  DIETARY: 'Dietary',
  CULTURAL: 'Cultural',
  OTHER: 'Other',
};

/**
 * MRN and the PHI identifiers.
 *
 * `undefined` and `null` mean different things here and are rendered
 * differently: the redaction interceptor removes the key entirely for a caller
 * without `client:read_phi`, so a missing key is "you may not see this" while a
 * null is "nobody has recorded one". Collapsing the two would tell a
 * coordinator a client has no Medicaid ID when the truth is that they cannot be
 * shown it.
 */
function IdentifiersCard({ detail }: { detail: ClientDetail }): ReactNode {
  const redacted = detail.medicaidId === undefined && detail.ssnLast4 === undefined;

  // The MRN is not repeated here. It is already in the header, it is not PHI,
  // and putting it in both places made it match twice on the page — which two
  // existing tests caught immediately, and which a reader would have found
  // merely untidy.
  const rows: [string, string][] = redacted
    ? []
    : [
        ['Medicaid ID', detail.medicaidId ?? 'Not recorded'],
        ['Medicare ID', detail.medicareId ?? 'Not recorded'],
        ['SSN last 4', detail.ssnLast4 ?? 'Not recorded'],
      ];

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Identifiers</h2>

      <dl className="mt-3 grid gap-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-3 text-sm">{label}</dt>
            <dd className="text-ink font-mono text-sm">{value}</dd>
          </div>
        ))}
      </dl>

      {redacted && (
        <p className="text-ink-3 mt-3 text-sm">
          Medicaid ID, Medicare ID and SSN last 4 are hidden. Seeing them needs the clinical-records
          permission.
        </p>
      )}
    </Card>
  );
}

function Overview({
  detail,
  clientId,
  canEdit,
}: {
  detail: ClientDetail;
  clientId: string;
  canEdit: boolean;
}): ReactNode {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {/*
        The identifiers, on screen.

        They were carried by the API, redacted correctly and asserted in the
        integration suite — and rendered nowhere, so field-level redaction was
        protecting fields no user could see either way. Exit criterion 7 is a
        claim about what a person without `client:read_phi` sees, and it cannot
        be walked against a screen that shows nobody anything.

        They earn their place beyond the criterion too: a Medicaid ID is what a
        care manager checks eligibility against, and it lived only in the
        database.
      */}
      <IdentifiersCard detail={detail} />

      <AddressesCard clientId={clientId} addresses={detail.addresses} canEdit={canEdit} />

      <ContactsCard clientId={clientId} contacts={detail.contacts} canEdit={canEdit} />

      <ClientRelationshipsCard clientId={clientId} />

      {/*
        Preferences were written by the intake wizard and displayed nowhere.
        Showing them closes the loop: a coordinator who typed "Spanish" into
        intake can see it was kept, and a scheduler sees it before assigning
        somebody who does not speak it.

        `isHardRequirement` is marked because the two are different
        instructions — a preference the scheduler may override, or a constraint
        they may not — and a list that rendered them identically would make the
        distinction unusable.
      */}
      <Card>
        <h2 className="text-ink text-lg font-bold">Preferences</h2>
        <p className="text-ink-3 mt-1 text-sm">
          What this client asks for. A requirement narrows who can be assigned; a preference does
          not.
        </p>
        {(detail.preferences ?? []).length === 0 ? (
          <p className="text-ink-3 mt-3 text-sm">None recorded.</p>
        ) : (
          <ul className="mt-3 grid gap-2">
            {(detail.preferences ?? []).map((preference) => (
              <li key={preference.id} className="text-ink-2 text-sm">
                <span className="text-ink font-medium">
                  {PREFERENCE_LABEL[preference.key] ?? preference.key}
                </span>
                {' · '}
                {preference.value}
                {preference.isHardRequirement && <span className="text-ink-3"> · required</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AllergiesCard
        clientId={clientId}
        allergies={detail.allergies}
        canEdit={canEdit}
        restricted={detail.allergies === undefined}
      />

      <MedicationsCard
        clientId={clientId}
        medications={detail.medications}
        canEdit={canEdit}
        restricted={detail.medications === undefined}
      />

      {/*
       * Full width, unlike its two neighbours.
       *
       * Sixteen activities in two groups do not read in a half-width column,
       * and the edit form is sixteen selects. Allergies and medications are
       * short lists and pair naturally; this is the third thing a caregiver is
       * briefed from and it is the largest of the three.
       */}
      <div className="lg:col-span-2" data-testid="assistance-profile">
        <AssistanceProfileCard
          clientId={clientId}
          entries={detail.assistanceProfile}
          canEdit={canEdit}
          restricted={detail.assistanceProfile === undefined}
        />
      </div>
    </div>
  );
}

function History({ clientId }: { clientId: string }): ReactNode {
  const api = useApi();

  const history = useQuery({
    queryKey: ['clients', 'history', clientId],
    queryFn: () =>
      /*
       * `entries`, not `history`.
       *
       * This read `history.data?.history`, which the endpoint has never
       * returned — `clientStatusHistorySchema` is `{ entries: [...] }`. So the
       * tab rendered "Nothing has changed yet" for every client, always.
       *
       * It went unnoticed because no client could have any history: nothing in
       * the console could change a status, so the empty state was correct by
       * accident for as long as the feature above it was missing. Two bugs
       * cancelling out is how one of them survives review.
       */
      api.get<{
        entries: {
          id: string;
          fromStatus: string | null;
          toStatus: string;
          reason: string;
          effectiveAt: string;
        }[];
      }>(`/v1/clients/${clientId}/history`),
  });

  if (history.isPending) {
    return (
      <Card>
        <p className="text-ink-3 py-8 text-center text-sm">Loading the history…</p>
      </Card>
    );
  }

  const entries = history.data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          title="Nothing has changed yet"
          explanation="Every status change is recorded here with who made it and why, which is the first thing an auditor asks for."
        />
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Status history</h2>
      <ol className="mt-4 grid gap-4">
        {entries.map((entry) => (
          <li key={entry.id} className="border-line border-l-edge pl-4">
            <Eyebrow>{new Date(entry.effectiveAt).toLocaleDateString()}</Eyebrow>
            <p className="text-ink mt-1 text-sm font-medium">
              {entry.fromStatus ? `${STATUS_LABEL[entry.fromStatus] ?? entry.fromStatus} → ` : ''}
              {STATUS_LABEL[entry.toStatus] ?? entry.toStatus}
            </p>
            <p className="text-ink-2 mt-1 text-sm">{entry.reason}</p>
          </li>
        ))}
      </ol>
    </Card>
  );
}
